/**
 * Request Approval button component
 * Shows on draft products when validation checks pass
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PrimaryButtonMediumV2 } from "../../../../components/Buttons/Buttons";
import { useApprovalEligibilityContext } from "../Approvals/ApprovalEligibilityContext";
import type { PIMProduct } from "../../../../types/types.PIM";
import { RequestApprovalModal } from "./RequestApprovalModal";
import ReactTooltip from "react-tooltip";

const PaddedButton = styled(PrimaryButtonMediumV2)`
  margin: 0 ${({ theme }) => theme.resolve("spacing_xs")};
`;

function hasEditableSkuCollection(product: PIMProduct): boolean {
  const skuCollection = product.product_schema.collections.find(
    (c) => c.object_type === "skus"
  );
  if (!skuCollection) {
    return false;
  }

  const columns = skuCollection.columns.filter(({ is_visible }) => is_visible);
  return !columns.every((col) => col.is_editable === false);
}

interface RequestApprovalButtonProps {
  product: PIMProduct;
  productId: string;
  onApprovalRequested?: () => void;
}

export function RequestApprovalButton({
  product,
  productId,
  onApprovalRequested,
}: RequestApprovalButtonProps) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  // get the approval eligibility context
  const { eligible, enabled, isLoading, triggerUpdate } =
    useApprovalEligibilityContext();

  const handleClick = () => {
    if (eligible && enabled) {
      setShowModal(true);
    }
  };

  const handleApprovalSubmitted = async () => {
    setShowModal(false);
    onApprovalRequested?.();
    // update the approval eligibility context
    await triggerUpdate();
  };

  if (isLoading || !eligible || !hasEditableSkuCollection(product)) {
    return <></>;
  }

  return (
    <>
      <PaddedButton
        onClick={handleClick}
        disabled={!enabled}
        data-for={`request-approval-${productId}`}
        data-tip={
          !enabled
            ? t("Division should be set for the SKU to request approval")
            : ""
        }
      >
        {t("Request Approval")}
      </PaddedButton>
      <ReactTooltip
        id={`request-approval-${productId}`}
        effect="solid"
        padding={"10px"}
      />
      {showModal && (
        <RequestApprovalModal
          productId={productId}
          onClose={() => setShowModal(false)}
          onSubmit={handleApprovalSubmitted}
        />
      )}
    </>
  );
}
